import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Sparkles, 
  Camera, 
  Star, 
  X, 
  ArrowRight,
  Clock,
  Gift,
  Zap
} from 'lucide-react';
import { Button } from './ui/button';

/**
 * Welcome Modal - Shows on first login for new users
 * Highlights the 7-day premium trial and encourages AI Scanner usage
 */
export function WelcomeModal({ user, onClose }) {
  const navigate = useNavigate();
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    // Animate in after mount
    const timer = setTimeout(() => setIsVisible(true), 100);
    return () => clearTimeout(timer);
  }, []);

  const handleClose = () => {
    setIsVisible(false);
    setTimeout(onClose, 300); // Wait for animation to complete
  };

  const handleTryScanner = () => {
    handleClose();
    navigate('/list/photos');
  };

  const handleViewHelp = () => {
    handleClose();
    navigate('/help?guide=ai-photo-scanner');
  };

  const trialDaysRemaining = user?.trial_days_remaining || 7;

  return (
    <div 
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 overflow-y-auto ${
        isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div 
        className={`relative w-full max-w-lg my-auto bg-[var(--theme-card)] rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 max-h-[90vh] overflow-y-auto ${
          isVisible ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 translate-y-4'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button - sticky on mobile */}
        <button
          onClick={handleClose}
          className="absolute top-3 right-3 sm:top-4 sm:right-4 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors z-10"
          data-testid="welcome-modal-close"
        >
          <X className="w-4 h-4 text-white" />
        </button>

        {/* Header with gradient - more compact on mobile */}
        <div className="bg-gradient-to-br from-[#1E3A5F] to-[#2D4A6F] p-4 sm:p-6 pb-6 sm:pb-8 text-white">
          <div className="flex items-center gap-3 mb-2 sm:mb-3">
            <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-white/10 flex items-center justify-center flex-shrink-0">
              <Gift className="w-5 h-5 sm:w-6 sm:h-6 text-[#C9956C]" />
            </div>
            <div>
              <h2 className="text-xl sm:text-2xl font-bold">Welcome to WANTD!</h2>
              <p className="text-white/70 text-sm">Hey {user?.first_name || user?.username} 👋</p>
            </div>
          </div>
        </div>

        {/* Trial banner - more compact on mobile */}
        <div className="bg-[#C9956C]/10 border-b border-[#C9956C]/20 px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 rounded-full bg-[#C9956C]/20 flex items-center justify-center flex-shrink-0">
              <Star className="w-4 h-4 sm:w-5 sm:h-5 text-[#C9956C]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-[var(--theme-text-primary)] text-sm sm:text-base">
                Your 7-Day Premium Trial is Active!
              </p>
              <p className="text-xs sm:text-sm text-[var(--theme-text-secondary)]">
                <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 inline mr-1" />
                {trialDaysRemaining} days remaining
              </p>
            </div>
          </div>
        </div>

        {/* Content - scrollable area */}
        <div className="p-4 sm:p-6">
          <p className="text-sm sm:text-base text-[var(--theme-text-secondary)] mb-4 sm:mb-6">
            You have <span className="font-semibold text-[var(--theme-text-primary)]">free access to all Premium features</span> for the next {trialDaysRemaining} days.
          </p>

          {/* Feature highlights - more compact on mobile */}
          <div className="space-y-2 sm:space-y-3 mb-4 sm:mb-6">
            <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-[var(--theme-hover)]">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#C9956C]/10 flex items-center justify-center flex-shrink-0">
                <Camera className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#C9956C]" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm sm:text-base text-[var(--theme-text-primary)]">AI Photo Scanner</p>
                <p className="text-xs sm:text-sm text-[var(--theme-text-secondary)]">
                  Snap photos - our AI creates listings automatically
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-[var(--theme-hover)]">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#059669]/10 flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#059669]" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm sm:text-base text-[var(--theme-text-primary)]">Smart Matching</p>
                <p className="text-xs sm:text-sm text-[var(--theme-text-secondary)]">
                  Get priority notifications when items match with buyers
                </p>
              </div>
            </div>

            <div className="flex items-start gap-2 sm:gap-3 p-2 sm:p-3 rounded-xl bg-[var(--theme-hover)]">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-[#1E3A5F]/10 flex items-center justify-center flex-shrink-0">
                <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-[#1E3A5F]" />
              </div>
              <div className="min-w-0">
                <p className="font-medium text-sm sm:text-base text-[var(--theme-text-primary)]">5 Special Interests / Week</p>
                <p className="text-xs sm:text-sm text-[var(--theme-text-secondary)]">
                  Priority alerts for specific items you want
                </p>
              </div>
            </div>
          </div>

          {/* CTAs */}
          <div className="space-y-2 sm:space-y-3">
            <Button
              onClick={handleTryScanner}
              className="w-full bg-[#C9956C] hover:bg-[#B8845B] text-white h-10 sm:h-12 rounded-full font-medium text-sm sm:text-base"
              data-testid="welcome-try-scanner-btn"
            >
              <Camera className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
              Try AI Photo Scanner
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            
            <Button
              onClick={handleViewHelp}
              variant="outline"
              className="w-full h-9 sm:h-11 rounded-full border-[var(--theme-border)] text-sm sm:text-base"
              data-testid="welcome-learn-more-btn"
            >
              Learn how it works
            </Button>
          </div>

          {/* Footer note */}
          <p className="text-xs text-center text-[var(--theme-text-muted)] mt-3 sm:mt-4">
            No credit card required. Your trial expires automatically.
          </p>
        </div>
      </div>
    </div>
  );
}

export default WelcomeModal;
